/**
 * Edit one space (RESTORE_PLAN R1.5, from the pre-November ProjectDialog): name, description,
 * type (incl. Jira Product Discovery), key with the key guidance, the optional Jira space URL.
 * Save stays off until something changed; name and key are checked against the company's other
 * spaces as you type (the server checks again).
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from './ui/utils';
import { SpaceKeyGuidance } from './SpaceKeyGuidance';
import {
  SPACE_TYPE_OPTIONS,
  changedSpaceDraft,
  jiraSpaceUrlProblem,
  spaceDraftFrom,
  spaceKeyProblem,
  spaceTypeOption,
  type SpaceEditDraft,
  type SpaceType,
} from '../utils/spaceTeams';
import { updateSpace } from '../utils/spaceTeamsApi';

export interface EditableSpace {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  type?: string;
  key?: string;
  jiraSpaceUrl?: string;
  status?: string;
}

interface SpaceEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  space: EditableSpace | null;
  /** The company's other spaces, for the live name and key check. */
  otherSpaces: { id: string; name: string; key?: string; status?: string }[];
  accessToken: string;
  onSaved: (space: Record<string, unknown>) => void | Promise<void>;
}

export function SpaceEditDialog({ open, onOpenChange, space, otherSpaces, accessToken, onSaved }: SpaceEditDialogProps) {
  const original = useMemo(() => spaceDraftFrom(space), [space]);
  const [draft, setDraft] = useState<SpaceEditDraft>(original);
  const [saving, setSaving] = useState(false);
  const [guidanceOpen, setGuidanceOpen] = useState(false);

  useEffect(() => {
    if (open) setDraft(original);
  }, [open, original]);

  const active = otherSpaces.filter((s) => s.id !== space?.id && (s.status ?? 'active') !== 'archived');
  const nameTaken = !!draft.name.trim() && active.some((s) => s.name.trim().toLowerCase() === draft.name.trim().toLowerCase());
  const keyNorm = draft.key.trim().toUpperCase();
  const keyTaken = !!keyNorm && active.some((s) => String(s.key ?? '').trim().toUpperCase() === keyNorm);
  const keyProblem = spaceKeyProblem(draft.key);
  const urlProblem = jiraSpaceUrlProblem(draft.jiraSpaceUrl);
  const changes = changedSpaceDraft(original, draft);
  const changed = Object.keys(changes).length > 0;
  const canSave = changed && !!draft.name.trim() && !nameTaken && !keyTaken && !keyProblem && !urlProblem && !saving;

  const save = async () => {
    if (!space || !canSave) return;
    try {
      setSaving(true);
      const { space: saved } = await updateSpace(space.companyId, space.id, changes, accessToken);
      toast.success(`Space "${draft.name.trim()}" updated`);
      onOpenChange(false);
      await onSaved(saved);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not update the space');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Space</DialogTitle>
          <DialogDescription>Update the space details. Teams are managed on the space's Teams list.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="edit-space-name">Space Name *</Label>
            <Input
              id="edit-space-name"
              value={draft.name}
              autoComplete="off"
              aria-invalid={nameTaken || !draft.name.trim()}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              className={cn(nameTaken && 'border-destructive')}
            />
            {nameTaken && (
              <p className="text-xs text-destructive flex items-center gap-1.5">
                <AlertCircle className="size-3.5" aria-hidden />
                A space with this name already exists for this client.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-space-description">Description</Label>
            <Textarea
              id="edit-space-description"
              value={draft.description}
              rows={3}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-space-type">Space Type *</Label>
            <Select value={draft.type} onValueChange={(value: SpaceType) => setDraft((d) => ({ ...d, type: value }))}>
              <SelectTrigger id="edit-space-type" aria-label="Space Type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPACE_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{spaceTypeOption(draft.type)?.description}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="edit-space-key">Space Key *</Label>
              <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setGuidanceOpen(true)}>
                <HelpCircle className="size-3.5 mr-1" aria-hidden />
                Key guidance
              </Button>
            </div>
            <Input
              id="edit-space-key"
              value={draft.key}
              maxLength={10}
              aria-invalid={!!keyProblem || keyTaken}
              onChange={(e) => setDraft((d) => ({ ...d, key: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
              className={cn((keyProblem || keyTaken) && 'border-destructive')}
            />
            {(keyProblem || keyTaken) ? (
              <p className="text-xs text-destructive">{keyTaken ? 'This key is already used by another space.' : keyProblem}</p>
            ) : changes.key ? (
              <p className="text-xs text-amber-700 flex items-start gap-1.5" data-testid="space-key-change-warning">
                <AlertTriangle className="size-3.5 shrink-0 mt-0.5" aria-hidden />
                Changing a key that already exists in Jira re-indexes its issues and breaks links, filters and boards
                that use the old key.
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-space-url">Jira Space URL (Optional)</Label>
            <Input
              id="edit-space-url"
              value={draft.jiraSpaceUrl}
              placeholder="e.g., https://yourcompany.atlassian.net/jira/servicedesk/projects/SD"
              aria-invalid={!!urlProblem}
              onChange={(e) => setDraft((d) => ({ ...d, jiraSpaceUrl: e.target.value }))}
              className={cn(urlProblem && 'border-destructive')}
            />
            <p className={cn('text-xs', urlProblem ? 'text-destructive' : 'text-muted-foreground')}>
              {urlProblem || 'Link to the space in Jira, for easy reference during implementation'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={!canSave}>
            {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
        <SpaceKeyGuidance open={guidanceOpen} onClose={() => setGuidanceOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}
